import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';

import { GemsPack } from '../BuyPacks/GemsPack';
import type {
  BuyGemsDialogQuery,
  BuyGemsDialogQueryVariables,
} from './__generated__/index.graphql';

const DialogBody = styled(Vertical)`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: var(--double-unit);
  padding: 0 var(--triple-unit) var(--triple-unit) var(--triple-unit);
`;
const GemsPackContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, calc(50% - var(--unit)));
  gap: var(--double-unit);
  min-width: min(400px, calc(var(--100vw) - 6 * var(--unit)));
`;

const QUERY = gql`
  query BuyGemsDialogQuery($sport: Sport!) {
    market {
      id
      gemsPacks: inGameCurrencyPacks(currency: COMMON_GEM) {
        id

        baseAmount
        ...GemsPack_inGameCurrencyPack
      }
    }
    tokens {
      purchasableItems(sport: $sport) {
        slug
        ... on InGameCurrencyPurchasableItem {
          slug
          baseAmount
          ...GemsPack_inGameCurrencyPurchasableItem
        }
      }
    }
  }
  ${GemsPack.fragments.inGameCurrencyPack}
  ${GemsPack.fragments.inGameCurrencyPurchasableItem}
` as TypedDocumentNode<BuyGemsDialogQuery, BuyGemsDialogQueryVariables>;

export const BuyGemsDialog = () => {
  const {
    flags: { usePurchasableItemId = false },
  } = useFeatureFlags();

  const closeDialog = useCloseDialog();
  const onClose = () => closeDialog(DialogKey.buyGems);

  const { sport } = useSportContext();
  const { data } = useQuery(QUERY, { variables: { sport } });
  const { market, tokens } = data || {};

  const gemsPacks = usePurchasableItemId
    ? tokens?.purchasableItems
        .map(item =>
          isType(item, 'InGameCurrencyPurchasableItem') ? item : null
        )
        .filter(Boolean) || []
    : market?.gemsPacks;

  const sortedGemsPacks = [...(gemsPacks || [])].sort((a, b) => {
    const aAmount = a.baseAmount;
    const bAmount = b.baseAmount;
    return aAmount - bAmount;
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        <Horizontal center>
          <LabelL bold>
            <FormattedMessage id="GemsPacks.title" defaultMessage="Buy Gems" />
          </LabelL>
        </Horizontal>
      }
    >
      <DialogBody>
        <GemsPackContainer>
          {sortedGemsPacks.map(gemsPack => (
            <GemsPack key={gemsPack.id} gemsPack={gemsPack} onClose={onClose} />
          ))}
        </GemsPackContainer>
      </DialogBody>
    </Dialog>
  );
};
